import React, { useContext, useEffect, useState } from 'react';
import { IPaginationParams } from '../../../services/types';
import { AppContext } from '../../../context/AppContextProvider';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { Add } from '@mui/icons-material';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import {
  useGetAllRecognitionGroupByLicenseAsync,
  useGetAllRecognitionGroupByLicenseCountAsync,
} from '../../../services/Recognition/Group/hooks/useQuery';
import {
  useDeleteRecognitionGroupAsync,
  useGetRecognitionGroupAsync,
} from '../../../services/Recognition/Group/hooks/useMutations';
import RecognitionGroupEditModal from './_components/RecognitionGroupEditModal';
import {
  IRecognitionGroupFilterParams,
  IRecognitionGroupProps,
  IRecognitionGroupSimpleProps,
} from '../../../services/Recognition/Group/types';

const RecognitionGroupsList = () => {
  const [filter, setFilter] = useState<IRecognitionGroupFilterParams>({
    search: '',
  });
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isVisibleEditModal, setIsVisibleEditModal] = useState<boolean>(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [selectedToEdit, setSelectedToEdit] =
    useState<IRecognitionGroupProps>();
  const [selectedToDelete, setSelectedToDelete] =
    useState<IRecognitionGroupSimpleProps>();
  const {
    data: groups,
    refetch: refetchAllGroups,
    isLoading: isLoadingGroups,
  } = useGetAllRecognitionGroupByLicenseAsync(paginationParams, filter);
  const {
    data: totalGroups,
    refetch: refetchGroupsCount,
    isLoading: isLoadingCount,
  } = useGetAllRecognitionGroupByLicenseCountAsync(paginationParams, filter);
  const { mutateAsync: mutateGetUnity } = useGetRecognitionGroupAsync();
  const { mutateAsync: mutateDeleteUnity } = useDeleteRecognitionGroupAsync();

  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const columns: IColumnProps[] = [
    { id: 'name', label: 'Nome', width: 'auto' },
    {
      id: 'description',
      label: 'Descrição',
      width: 'auto',
      render: (description) => {
        if (description) {
          return <>{description}</>;
        } else {
          return <>Não Possui</>;
        }
      },
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: '0px',
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          isWithDefaultOptionsList
          handleDelete={() => handleDelete(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const refetchGroups = async () => {
    await refetchGroupsCount();
    await refetchAllGroups();
  };

  const handleEdit = async (id: number) => {
    setIsShowLoadingComponent(true);
    await mutateGetUnity(id)
      .then(setSelectedToEdit)
      .finally(() => {
        setIsShowLoadingComponent(false);
        setIsVisibleEditModal(false);
      });
    setIsVisibleEditModal(true);
  };

  const handleDelete = async (id: number) => {
    if (groups) setSelectedToDelete(groups.find((i) => i.id === id));
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    setIsShowLoadingComponent(true);
    if (selectedToDelete && selectedToDelete.id) {
      await mutateDeleteUnity(selectedToDelete.id)
        .then(() => {
          setSelectedToEdit(undefined);
          setIsVisibleDeleteModal(false);
          showSnackbar('Grupo facial deletado com sucesso!', 'success');
          refetchGroups();
        })
        .finally(() => {
          setIsShowLoadingComponent(false);
          setIsVisibleDeleteModal(false);
        });
    }
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteModal(false);
    setSelectedToEdit(undefined);
  };

  const handleSaveForm = async () => {
    setSelectedToEdit(undefined);
    await refetchGroups();
    setIsShowLoadingComponent(false);
  };

  const handleCloseEditionModal = () => {
    setSelectedToEdit(undefined);
    setIsVisibleEditModal(false);
  };

  useEffect(() => {
    refetchGroups();
  }, [paginationParams, filter]);

  return (
    <>
      <TableComponent
        columns={columns}
        rows={groups ?? []}
        sortByName={'name'}
        isLoading={isLoadingGroups || isLoadingCount}
        title="Grupos Faciais"
        totalCount={totalGroups}
        onSubmitMainFilter={refetchGroups}
        searchValue={filter.search}
        onChangeMainFilter={(search) =>
          setFilter((prev) => ({ ...prev, search }))
        }
        onChangePageOrRowsPerPage={setPaginationParams}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            Novo Grupo Facial
          </ButtonComponent>
        }
      />
      <ConfirmDeleteModalComponent
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja deletar o grupo facial ${selectedToDelete?.name} ?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title={'Deletar Grupo Facial'}
      />
      <RecognitionGroupEditModal
        isOpen={isVisibleEditModal}
        setIsOpen={setIsVisibleEditModal}
        onClose={handleCloseEditionModal}
        selectedGroup={selectedToEdit}
        handleSaveForm={handleSaveForm}
      />
    </>
  );
};

export default RecognitionGroupsList;
